/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import { getPathUrlAsString, getRuleUrl } from '~shared/helpers/urls';
import {
  JiraBulkWorkItemQueryResult,
  JiraLinkedResource,
  JiraPermission,
  JiraWorkItem,
  JiraWorkItemPayload,
  JiraWorkItemResourceType,
  JiraWorkType,
  LinkableIssue,
} from '../types';

export const JIRA_SUMMARY_FIELD = 'summary';
export const JIRA_DESCRIPTION_FIELD = 'description';
export const JIRA_ISSUE_TYPE_FIELD = 'issuetype';
export const JIRA_PROJECT_FIELD = 'project';
export const JIRA_REPORTER_FIELD = 'reporter';

export const LINKED_TICKET_STATUSES = ['LINKED', 'NOT_LINKED'];

/*
 * There's no organization on SQS but as the API implementation is aligned between SQS and SQC,
 *  it needs an organization UUID to be passed, so we create a zero-value UUID to emulate an organization UUID.
 */
export const JIRA_INTEGRATION_ORGANIZATION_UUID = '00000000-0000-0000-0000-000000000000';

/*
 * Creates a hyperlink compatible with the syntax expected by Jira tickets
 */
export function createJiraHyperlink(linkText: string, url: string): string {
  return `[${linkText}|${url}]`;
}

function makeJiraPayloadForIssue(params: {
  email?: string;
  isLoggedIn: boolean;
  issue: LinkableIssue;
  workTypeId: string;
}) {
  const { issue, isLoggedIn, workTypeId, email } = params;
  const issueUrl = issue.url;
  const ruleKey = issue.ruleKey ?? '';
  const jiraWorkItemPayload: JiraWorkItemPayload = {
    summary: `SonarQube issue - ${issue.message}`,
    description: `
*SonarQube issue link:*
${createJiraHyperlink(issue.message, issueUrl)}

*Where is the issue?*
- File: ${issue.projectKey} > ${issue.componentPath}
- ${
      issue.textRange?.startLine === issue.textRange?.endLine
        ? `Code line: ${issue.textRange?.startLine}`
        : `Code lines: ${issue.textRange?.startLine} to ${issue.textRange?.endLine}`
    }
- Introduced on: ${new Date(issue.creationDate).toLocaleString('en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })}, ${new Date(issue.creationDate).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })}

*Why is this an issue and how to fix it?*
${createJiraHyperlink(ruleKey, getPathUrlAsString(getRuleUrl(ruleKey, issue.organization), false))}

*Impact*
${issue.impacts.map((impact) => `- Software quality - ${impact.softwareQuality}`).join('\n')}

*Creator:* ${isLoggedIn && email ? email : 'Unknown'}
`,
    sonarProjectId: issue.projectUuid,
    resourceId: issue.key,
    resourceType: JiraWorkItemResourceType.SonarIssue,
    workTypeId,
  };
  return jiraWorkItemPayload;
}

function makeJiraPayloadForRisk(params: {
  email?: string;
  isLoggedIn: boolean;
  issue: LinkableIssue;
  workTypeId: string;
}) {
  const { issue, isLoggedIn, workTypeId, email } = params;
  const issueUrl = issue.url;
  const jiraWorkItemPayload: JiraWorkItemPayload = {
    summary: `SonarQube dependency risk - ${issue.message}`,
    description: `
*SonarQube dependency risk link:*
${createJiraHyperlink(issue.message, issueUrl)}

*Where is the risk?*
- Project: ${issue.projectKey}
- Package: ${issue.packageUrl ?? 'Unknown'}
- Introduced on: ${new Date(issue.creationDate).toLocaleString('en-US', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })}, ${new Date(issue.creationDate).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })}

*Risk details:*
${createJiraHyperlink('View risk details', issueUrl)}

*Creator:* ${isLoggedIn && email ? email : 'Unknown'}
`,
    sonarProjectId: issue.projectUuid,
    resourceId: issue.key,
    resourceType: JiraWorkItemResourceType.DependencyRisk,
    workTypeId,
  };
  return jiraWorkItemPayload;
}

export function makeJiraPayload(params: {
  email?: string;
  isLoggedIn: boolean;
  issue: LinkableIssue;
  resourceType: JiraWorkItemResourceType;
  workTypeId: string;
}) {
  return params.resourceType === JiraWorkItemResourceType.DependencyRisk
    ? makeJiraPayloadForRisk(params)
    : makeJiraPayloadForIssue(params);
}

function hasJiraPermission(userActions: JiraPermission[], permission: JiraPermission) {
  return userActions.includes(permission);
}

export function getJiraPermissions(userActions: JiraPermission[]) {
  return {
    hasJiraCreatePermission: hasJiraPermission(userActions, JiraPermission.CREATE),
    hasJiraDeletePermission: hasJiraPermission(userActions, JiraPermission.DELETE),
  };
}

export function getSelectedWorkTypeIds(workTypes: JiraWorkType[] = []) {
  return workTypes.filter((wt) => wt.selected).map((wt) => wt.id);
}

/**
 * Returns the mandatory fields for a given Jira work type.
 * Mandatory fields are those that are required and that we cannot prefill when creating a work item.
 * Fields such as summary, description, issue type and project are mandatory, but we can prefill them;
 * therefore, they are excluded from the returned list.
 * Fields with default values are also excluded as they don't require user input.
 *
 * @param workType - The Jira work type to check.
 * @returns An array of mandatory fields for the work type.
 */
export function getJiraWorkTypeMandatoryFields(workType: JiraWorkType) {
  const excludedFields = new Set([
    JIRA_SUMMARY_FIELD,
    JIRA_DESCRIPTION_FIELD,
    JIRA_ISSUE_TYPE_FIELD,
    JIRA_PROJECT_FIELD,
    JIRA_REPORTER_FIELD,
  ]);
  return workType.fields.filter(
    (field) =>
      field.required && !excludedFields.has(field.key.toLowerCase()) && !field.hasDefaultValue,
  );
}

export function mapToLinkedSonarIssue(
  results: JiraBulkWorkItemQueryResult[],
): JiraLinkedResource[] {
  // Deduplicate work items based on jiraIssueId, jiraIssueKey, and jiraIssueUrl
  const uniqueWorkItems = new Map<string, JiraWorkItem>();

  results.forEach((result) => {
    const key = `${result.jiraIssueId}|${result.jiraIssueKey}|${result.jiraIssueUrl}`;

    // Reuse existing work item if it already exists, otherwise create a new one
    if (!uniqueWorkItems.has(key)) {
      const workItem: JiraWorkItem = {
        id: result.jiraIssueId ?? '', // Use jira issue id as the work item id
        jiraIssueId: result.jiraIssueId ?? '',
        jiraIssueKey: result.jiraIssueKey,
        jiraIssueUrl: result.jiraIssueUrl,
        jiraIssueStatus: 'Open', // Default status since it's not in the result
      };
      uniqueWorkItems.set(key, workItem);
    }
  });

  // Create JiraLinkedSonarIssue objects mapping each sonar issue to its work item
  return results.map((result) => {
    const key = `${result.jiraIssueId}|${result.jiraIssueKey}|${result.jiraIssueUrl}`;
    const workItem = uniqueWorkItems.get(key) as JiraWorkItem;

    return {
      jiraWorkItem: workItem,
      resourceId: result.sonarIssueKey,
      resourceType: JiraWorkItemResourceType.SonarIssue,
    };
  });
}
